/**
 * The HUD (UI-002): shots taken, balls left, and what the last shot did.
 *
 * Updated only from `session.settle()` — i.e. when the animation has finished — so it
 * never tells the player the result of a shot they are still watching
 * (ARCHITECTURE.md §5.1).
 *
 * Every string comes from `config/site.ts`; none is written here (CLAUDE.md §6).
 */
import { LABELS, RESULT_COPY } from '../config/site.ts';
import type { ShotOutcome } from '@potted/sim';

export interface Hud {
  update(shots: number, penalties: number, ballsRemaining: number): void;
  /** Say what just happened, briefly. `undefined` clears it. */
  flash(outcome: ShotOutcome | undefined): void;
  /**
   * UI-012: free play on the cleared table. The shot count is labelled "Free play",
   * shot messages stop (a scratch there costs nothing), and a Result button in the
   * middle brings the results back. `undefined` leaves free play.
   */
  setFreePlay(onResult: (() => void) | undefined): void;
  destroy(): void;
}

/**
 * UI-022: a number that changes pulses once, and a message fades in, so a pot or a
 * penalty is noticed without looking for it. Timings are the motion tokens, read from
 * the page, not copied here (CLAUDE.md §6). Nothing moves for a player who has asked
 * their device for less motion.
 */
function calm(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : true;
}

function motion(element: HTMLElement, keyframes: Keyframe[], token: string): void {
  if (calm() || typeof element.animate !== 'function') return;
  const root = getComputedStyle(document.documentElement);
  const duration = Number.parseFloat(root.getPropertyValue(token));
  const easing = root.getPropertyValue('--ease-out').trim();
  if (!(duration > 0)) return;
  element.animate(keyframes, { duration, easing: easing === '' ? 'ease-out' : easing });
}

const PULSE: Keyframe[] = [{ transform: 'scale(1.18)' }, { transform: 'scale(1)' }];
const ARRIVE: Keyframe[] = [
  { opacity: 0, transform: 'translateY(4px)' },
  { opacity: 1, transform: 'none' },
];

export function createHud(host: HTMLElement): Hud {
  host.innerHTML = `
    <p class="hud__stat">
      <span class="hud__label" data-shots-label>${LABELS.shots}</span>
      <span class="hud__value numeral" data-shots>0</span>
    </p>
    <p class="hud__flash" data-flash aria-live="polite"></p>
    <button type="button" class="btn btn--quiet hud__result" data-result hidden>${RESULT_COPY.showResult}</button>
    <p class="hud__stat hud__stat--end">
      <span class="hud__label">${LABELS.ballsRemaining}</span>
      <span class="hud__value numeral" data-remaining>—</span>
    </p>
  `;

  const shotsValue = need<HTMLSpanElement>(host, '[data-shots]');
  const remainingValue = need<HTMLSpanElement>(host, '[data-remaining]');
  const flashText = need<HTMLParagraphElement>(host, '[data-flash]');
  const shotsLabel = need<HTMLSpanElement>(host, '[data-shots-label]');
  const resultButton = need<HTMLButtonElement>(host, '[data-result]');
  let onResult: (() => void) | undefined;
  resultButton.addEventListener('click', () => onResult?.());
  // The first numbers shown are the table as it loads, not a change worth pointing at.
  let primed = false;

  const setValue = (element: HTMLElement, text: string): void => {
    if (element.textContent === text) return;
    element.textContent = text;
    if (primed) motion(element, PULSE, '--dur-slow');
  };
  const say = (text: string, tone: string): void => {
    flashText.textContent = text;
    flashText.dataset['tone'] = tone;
    if (text !== '') motion(flashText, ARRIVE, '--dur-base');
  };

  return {
    update(shots, penalties, ballsRemaining) {
      // Penalties are part of the score, so they are shown ON the shot count rather
      // than as a separate number a player has to add up themselves.
      setValue(
        shotsValue,
        penalties === 0 ? String(shots) : `${String(shots)} + ${String(penalties)}`,
      );
      shotsValue.classList.toggle('hud__value--penalised', penalties > 0);
      setValue(remainingValue, String(ballsRemaining));
      primed = true;
    },

    flash(outcome) {
      if (outcome === undefined || onResult !== undefined) {
        say('', '');
        return;
      }
      if (outcome.scratched) {
        say(`${LABELS.scratch} +1`, 'scratch');
        return;
      }
      if (outcome.pottedIds.length > 0) {
        say(
          outcome.pottedIds.length === 1 ? 'Potted' : `Potted ${String(outcome.pottedIds.length)}`,
          'pot',
        );
        return;
      }
      say('Missed', 'miss');
    },

    setFreePlay(next) {
      onResult = next;
      const free = next !== undefined;
      shotsLabel.textContent = free ? LABELS.freePlay : LABELS.shots;
      resultButton.hidden = !free;
      flashText.hidden = free;
      if (free) {
        flashText.textContent = '';
        flashText.dataset['tone'] = '';
      }
    },

    destroy() {
      host.innerHTML = '';
    },
  };
}

function need<T extends Element>(root: ParentNode, selector: string): T {
  const found = root.querySelector<T>(selector);
  if (found === null) throw new Error(`hud: ${selector} is missing`);
  return found;
}
